<template>
  <div>
    <Head :title="`Cuenta: ${form.name}`" />
    
    <!-- Header & Account info -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
        <div class="flex items-center gap-3">
          <h1 class="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            {{ form.name }}
            <img v-if="account.logo" :src="account.logo" alt="Logo" class="h-8 max-w-[120px] rounded-lg object-contain inline-block ml-1" />
          </h1>
          <span class="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-orange-100 text-orange-700">
            {{ subscription?.plan_name || account.plan_name || 'Cuenta' }}
          </span>
        </div>
        <p class="text-sm text-gray-500 mt-1">Configuración central, usuarios, suscripción e integraciones de tu negocio</p>
      </div>
    </div>

    <!-- Navegación de Pestañas (Pill Style con soporte para ?mode=...) -->
    <div class="mb-8 overflow-x-auto pb-2 scrollbar-hide">
      <nav class="flex space-x-2 min-w-max p-1.5 bg-gray-100/70 rounded-2xl w-fit border border-gray-200/50">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="[
            activeTab === tab.id
              ? 'bg-orange-500 text-white shadow-md shadow-orange-200 scale-102 font-extrabold'
              : 'text-gray-600 hover:bg-white hover:text-gray-900 font-semibold',
            'flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm transition-all duration-200'
          ]"
          @click="selectTab(tab.id)"
        >
          <i :class="tab.icon" class="text-xs" />
          {{ tab.name }}
        </button>
      </nav>
    </div>

    <!-- ══ TAB 1: INFORMACIÓN GENERAL ══ -->
    <div v-if="activeTab === 'informacion-general'" class="max-w-5xl bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300">
      <form @submit.prevent="updateAccount">
        <div class="p-8 md:p-10 space-y-10">
          <!-- Sección Datos de la Empresa -->
          <div>
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600">
                <i class="fas fa-building text-lg" />
              </div>
              <div>
                <h2 class="text-xl font-extrabold text-gray-900">Datos de la Empresa</h2>
                <p class="text-xs text-gray-400">Información fiscal, comercial y legal de tu negocio</p>
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <text-input
                v-model="form.name"
                :error="form.errors.name"
                label="Nombre Comercial del Negocio *"
                placeholder="Ej: Inversiones El Globo"
                class="[&_input]:rounded-xl"
                required
              />

              <text-input
                v-model="form.username"
                :error="form.errors.username"
                label="Usuario Único / Slug del Negocio"
                placeholder="Ej: el-globo"
                class="[&_input]:rounded-xl"
              />
              
              <text-input
                v-model="form.identification" 
                :error="form.errors.identification"
                label="RIF / Identificación Fiscal *"
                placeholder="J-12345678-0"
                class="[&_input]:rounded-xl"
              />

              <text-input
                v-model="form.reasonName"
                :error="form.errors.reasonName"
                label="Razón Social Legal *"
                placeholder="Denominación legal completa"
                class="[&_input]:rounded-xl"
                required
              />

              <text-input
                v-model="form.email"
                :error="form.errors.email"
                type="email"
                label="Correo Electrónico Corporativo *" 
                placeholder="contacto@tuempresa.com"
                class="[&_input]:rounded-xl"
              />

              <text-input
                v-model="form.phone"
                :error="form.errors.phone"
                label="Teléfono Principal de Contacto"
                placeholder="+58 424 000 0000"
                class="[&_input]:rounded-xl"
              />

              <select-input
                v-model="form.business_type"
                :error="form.errors.business_type"
                label="Sabor de la Cuenta / Rubro"
                class="[&_select]:rounded-xl"
              >
                <option :value="null">Seleccione el sabor del negocio...</option>
                <option v-for="type in businessTypes" :key="type.id" :value="type.id">{{ type.label }}</option>
              </select-input>

              <text-input
                v-model="form.address"
                :error="form.errors.address"
                label="Dirección Física Principal"
                placeholder="Ciudad, Estado, Dirección detallada"
                class="[&_input]:rounded-xl"
              />
            </div>
          </div>

          <!-- Separador -->
          <div class="border-t border-gray-100" />

          <!-- Sección Identidad Visual y Logo -->
          <div>
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-500">
                <i class="fas fa-brush text-lg" />
              </div>
              <div>
                <h3 class="text-xl font-extrabold text-gray-900">Personalización del Panel</h3>
                <p class="text-xs text-gray-400">Logo e imagen de marca para el panel de administración</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <text-input
                v-model="form.sidebarColor"
                :error="form.errors.sidebarColor"
                type="color"
                label="Color Barra Lateral"
                class="[&_input]:h-12 [&_input]:p-1 [&_input]:rounded-xl"
              />

              <text-input
                v-model="form.textSidebarColor"
                :error="form.errors.textSidebarColor"
                type="color" 
                label="Color Texto Menú"
                class="[&_input]:h-12 [&_input]:p-1 [&_input]:rounded-xl"
              />

              <text-input
                v-model="form.textHoverSidebarColor"
                :error="form.errors.textHoverSidebarColor"
                type="color"
                label="Color Hover Menú"
                class="[&_input]:h-12 [&_input]:p-1 [&_input]:rounded-xl"
              />
            </div>

            <div class="p-6 bg-gray-50/70 rounded-2xl border border-gray-100">
              <label class="block text-sm font-bold text-gray-700 mb-2">Logo Corporativo del Negocio</label>
              <div class="flex items-center gap-6 flex-wrap sm:flex-nowrap">
                <div class="w-20 h-20 rounded-2xl border-2 border-dashed border-gray-200 overflow-hidden flex items-center justify-center bg-white flex-shrink-0 shadow-sm">
                  <img v-if="account.logo" :src="account.logo" alt="Logo actual" class="w-full h-full object-contain p-1" />
                  <i v-else class="fas fa-image text-gray-300 text-2xl" />
                </div>
                <div class="flex-1">
                  <file-input
                    v-model="form.logo"
                    :errors="form.errors.logo ? [form.errors.logo] : []"
                    accept="image/*"
                    label=""
                  />
                  <p class="text-xs text-gray-400 mt-1">Este logo aparecerá en comprobantes de venta, panel y reportes oficiales.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Separador -->
          <div class="border-t border-gray-100" />

          <!-- Sección Configuraciones del Negocio / Sistema -->
          <div>
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                <i class="fas fa-sliders-h text-lg" />
              </div>
              <div>
                <h3 class="text-xl font-extrabold text-gray-900">Configuraciones Operativas del Sistema</h3>
                <p class="text-xs text-gray-400">Controla permisos comerciales y modalidades activas en tu plataforma</p>
              </div>
            </div>

            <div class="space-y-4">
              <!-- Item 1: Modificación de precios -->
              <div class="flex items-center justify-between p-5 rounded-2xl bg-gray-50/60 border border-gray-100 hover:bg-gray-50 transition-all">
                <div class="flex gap-4 items-start">
                  <div class="w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0 mt-0.5">
                    <i class="fas fa-tags text-sm" />
                  </div>
                  <div>
                    <p class="font-bold text-gray-900 text-sm">Modificación de Precios por Vendedores</p>
                    <p class="text-xs text-gray-500 max-w-lg leading-relaxed mt-0.5">Permite a los usuarios vendedores modificar el precio final de venta al momento de generar órdenes.</p>
                  </div>
                </div>
                <button
                  type="button"
                  class="relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 focus:outline-none flex-shrink-0"
                  :class="form.sellerCanModifyPrices ? 'bg-orange-500 shadow-sm shadow-orange-200' : 'bg-gray-200'"
                  @click="form.sellerCanModifyPrices = !form.sellerCanModifyPrices"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm"
                    :class="form.sellerCanModifyPrices ? 'translate-x-6' : 'translate-x-1'"
                  />
                </button>
              </div>

              <!-- Item 2: Agenda propia de clientes -->
              <div class="flex items-center justify-between p-5 rounded-2xl bg-gray-50/60 border border-gray-100 hover:bg-gray-50 transition-all">
                <div class="flex gap-4 items-start">
                  <div class="w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center text-blue-500 flex-shrink-0 mt-0.5">
                    <i class="fas fa-address-book text-sm" />
                  </div>
                  <div>
                    <p class="font-bold text-gray-900 text-sm">Agenda de Clientes Privada por Vendedor</p>
                    <p class="text-xs text-gray-500 max-w-lg leading-relaxed mt-0.5">Cada vendedor solo podrá visualizar y gestionar los clientes que él mismo haya registrado.</p>
                  </div>
                </div>
                <button
                  type="button"
                  class="relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 focus:outline-none flex-shrink-0"
                  :class="form.sellerHasOwnClientAgenda ? 'bg-orange-500 shadow-sm shadow-orange-200' : 'bg-gray-200'"
                  @click="form.sellerHasOwnClientAgenda = !form.sellerHasOwnClientAgenda"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm"
                    :class="form.sellerHasOwnClientAgenda ? 'translate-x-6' : 'translate-x-1'"
                  />
                </button>
              </div>

              <!-- Item 3: Tasas personalizadas -->
              <div class="flex items-center justify-between p-5 rounded-2xl bg-gray-50/60 border border-gray-100 hover:bg-gray-50 transition-all">
                <div class="flex gap-4 items-start">
                  <div class="w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center text-emerald-500 flex-shrink-0 mt-0.5">
                    <i class="fas fa-exchange-alt text-sm" />
                  </div>
                  <div>
                    <p class="font-bold text-gray-900 text-sm">Tasas de Cambio Personalizadas</p>
                    <p class="text-xs text-gray-500 max-w-lg leading-relaxed mt-0.5">Habilita la asignación de tasas de cambio internas personalizadas (ej. Paralelo vs BCV) para cálculos.</p>
                  </div>
                </div>
                <button
                  type="button"
                  class="relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 focus:outline-none flex-shrink-0"
                  :class="form.custom_rates ? 'bg-orange-500 shadow-sm shadow-orange-200' : 'bg-gray-200'"
                  @click="form.custom_rates = !form.custom_rates"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm"
                    :class="form.custom_rates ? 'translate-x-6' : 'translate-x-1'"
                  />
                </button>
              </div>

              <!-- Item 4: Modo Restaurante -->
              <div class="flex items-center justify-between p-5 rounded-2xl bg-gray-50/60 border border-gray-100 hover:bg-gray-50 transition-all">
                <div class="flex gap-4 items-start">
                  <div class="w-10 h-10 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center text-amber-500 flex-shrink-0 mt-0.5">
                    <i class="fas fa-utensils text-sm" />
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <p class="font-bold text-gray-900 text-sm">Modo Restaurante & Mesas en POS</p>
                      <span class="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[10px] font-black uppercase">Gastronomía</span>
                    </div>
                    <p class="text-xs text-gray-500 max-w-lg leading-relaxed mt-0.5">Habilita comandas por mesas, cuentas abiertas simultáneas y módulo de comandas en el Punto de Venta.</p>
                  </div>
                </div>
                <button
                  type="button"
                  class="relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 focus:outline-none flex-shrink-0"
                  :class="form.restaurant_mode ? 'bg-orange-500 shadow-sm shadow-orange-200' : 'bg-gray-200'"
                  @click="form.restaurant_mode = !form.restaurant_mode"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm"
                    :class="form.restaurant_mode ? 'translate-x-6' : 'translate-x-1'"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between px-8 py-5 bg-gray-50 border-t border-gray-100">
          <p class="text-xs text-gray-400 flex items-center gap-1.5">
            <i class="fas fa-shield-alt text-gray-400" />
            Tus datos corporativos se almacenan de forma segura.
          </p>
          <loading-button
            :loading="form.processing"
            class="btn-orange shadow-lg shadow-orange-100"
            type="submit"
          >
            <i class="fas fa-save mr-2" />
            Guardar Cambios de la Empresa
          </loading-button>
        </div>
      </form>
    </div>

    <!-- ══ TAB 2: PERFIL ══ -->
    <div v-if="activeTab === 'perfil'" class="max-w-4xl bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300">
      <form @submit.prevent="updateProfile">
        <div class="p-8 md:p-10 space-y-8">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center text-2xl font-black shadow-inner overflow-hidden border border-orange-200">
              <img v-if="authUser.photo" :src="authUser.photo" alt="Avatar" class="w-full h-full object-cover" />
              <span v-else>{{ (authUser.first_name?.[0] || 'U') }}{{ (authUser.last_name?.[0] || '') }}</span>
            </div>
            <div>
              <h2 class="text-2xl font-extrabold text-gray-900">{{ authUser.first_name }} {{ authUser.last_name }}</h2>
              <div class="flex items-center gap-2 mt-1">
                <span class="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-blue-50 text-blue-700">
                  {{ authUser.role === 'admin' ? 'Administrador' : authUser.role }}
                </span>
                <span v-if="authUser.organization" class="text-xs text-gray-400">
                  • Tienda: <strong>{{ authUser.organization.name }}</strong>
                </span>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
            <text-input
              v-model="profileForm.first_name"
              :error="profileForm.errors.first_name"
              label="Nombre *"
              placeholder="Tu nombre"
              class="[&_input]:rounded-xl"
              required
            />

            <text-input
              v-model="profileForm.last_name"
              :error="profileForm.errors.last_name"
              label="Apellido *"
              placeholder="Tu apellido"
              class="[&_input]:rounded-xl"
              required
            />

            <text-input
              v-model="profileForm.email"
              :error="profileForm.errors.email"
              type="email"
              label="Correo Electrónico de Acceso *"
              placeholder="correo@ejemplo.com"
              class="[&_input]:rounded-xl"
              required
            />

            <text-input
              v-model="profileForm.password"
              :error="profileForm.errors.password"
              type="password"
              autocomplete="new-password"
              label="Nueva Contraseña (Opcional)"
              placeholder="Dejar en blanco para mantener actual"
              class="[&_input]:rounded-xl"
            />

            <div class="md:col-span-2">
              <file-input
                v-model="profileForm.photo"
                :error="profileForm.errors.photo"
                type="file"
                accept="image/*"
                label="Cambiar Foto de Perfil"
                class="[&_input]:rounded-xl"
              />
              <p class="text-xs text-gray-400 mt-1">Formato JPG, PNG o WEBP. Recomendado 200×200px.</p>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end px-8 py-5 bg-gray-50 border-t border-gray-100">
          <loading-button
            :loading="profileForm.processing"
            class="btn-orange shadow-lg shadow-orange-100"
            type="submit"
          >
            <i class="fas fa-user-check mr-2" />
            Actualizar Mi Perfil
          </loading-button>
        </div>
      </form>
    </div>

    <!-- ══ TAB 3: MI SUSCRIPCIÓN ══ -->
    <div v-if="activeTab === 'mi-suscripcion'" class="max-w-5xl space-y-8 transition-all duration-300">
      <!-- Hero Card del Plan Actual -->
      <div class="relative overflow-hidden rounded-[32px] bg-gradient-to-r from-gray-900 via-gray-800 to-orange-950 p-8 md:p-10 shadow-2xl border border-white/10 group">
        <div class="pointer-events-none absolute -right-20 -top-20 w-80 h-80 bg-orange-500 opacity-15 rounded-full blur-3xl group-hover:opacity-25 transition-opacity duration-700" />
        
        <div class="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div class="flex items-center gap-6">
            <div class="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-3xl shadow-xl flex-shrink-0">
              <i class="fas fa-crown text-orange-400 drop-shadow-[0_0_12px_rgba(251,146,60,0.6)]" />
            </div>
            <div>
              <span class="text-orange-400 text-xs font-black uppercase tracking-[0.3em] mb-1 block">Plan Actual</span>
              <h2 class="text-4xl font-black text-white tracking-tight">{{ subscription?.plan_name || 'Plan Gratuito' }}</h2>
              <div class="flex items-center gap-3 mt-3 flex-wrap">
                <span v-if="subscription?.has_active_subscription" class="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-green-500/20 text-green-400 border border-green-500/30">
                  <i class="fas fa-check-circle mr-1" /> Suscripción Activa
                </span>
                <span v-else class="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  {{ subscription?.is_on_free_plan ? 'Plan Gratuito' : 'Pendiente de Renovación' }}
                </span>
                <span class="text-white/60 text-sm font-medium">
                  Precio: <strong class="text-white font-bold">${{ subscription?.plan_price || 0 }}/mes</strong>
                </span>
                <span v-if="subscription?.plan_expires_at" class="text-white/60 text-xs">
                  • Vence: {{ subscription.plan_expires_at }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-3 flex-wrap">
            <Link href="/subscription/plans" class="px-6 py-3.5 bg-orange-500 hover:bg-orange-400 text-white rounded-2xl font-bold text-xs uppercase tracking-wider transition-all shadow-xl shadow-orange-950/40 flex items-center gap-2">
              <i class="fas fa-exchange-alt" /> Cambiar Plan
            </Link>
            <Link v-if="subscription?.is_on_free_plan" href="/subscription/prime" class="px-6 py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-2xl font-bold text-xs uppercase tracking-wider transition-all border border-white/20">
              Ver Beneficios Prime
            </Link>
          </div>
        </div>
      </div>

      <!-- Beneficios incluidos en el plan -->
      <div class="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
        <h3 class="text-xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500">
            <i class="fas fa-star text-sm" />
          </div>
          Características Incluidas en tu Cuenta
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="(feat, idx) in (subscription?.plan_features || defaultPlanFeatures)" :key="idx"
            class="flex items-start gap-3 p-4 rounded-2xl bg-gray-50/60 border border-gray-100"
          >
            <i class="fas fa-check-circle text-green-500 mt-1 flex-shrink-0" />
            <span class="text-sm font-semibold text-gray-800">{{ feat }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ TAB 4: PANEL DE USUARIOS ══ -->
    <div v-if="activeTab === 'panel-usuarios'" class="max-w-5xl space-y-6 transition-all duration-300">
      <!-- Top Bar: Buscador y Botón Nuevo Usuario -->
      <div class="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-3 w-full md:w-auto">
          <div class="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
            <i class="fas fa-users-cog text-lg" />
          </div>
          <div>
            <h2 class="text-lg font-extrabold text-gray-900">Usuarios del Negocio</h2>
            <p class="text-xs text-gray-400">{{ filteredUsers.length }} usuarios registrados en esta cuenta</p>
          </div>
        </div>

        <div class="flex items-center gap-3 w-full md:w-auto flex-wrap sm:flex-nowrap">
          <!-- Filtro de Rol -->
          <select v-model="userFilterRole" class="border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400 bg-gray-50/50">
            <option value="">Todos los roles</option>
            <option value="admin">Administrador</option>
            <option value="sales">Vendedor</option>
            <option value="storer">Almacenista</option>
            <option value="financial">Finanzas</option>
          </select>

          <!-- Buscador -->
          <div class="relative flex-1 sm:w-64">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
              <i class="fas fa-search" />
            </span>
            <input
              v-model="userSearchQuery"
              type="text"
              placeholder="Buscar por nombre o correo..."
              class="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          <button class="btn-orange text-xs whitespace-nowrap shadow-sm shadow-orange-100" @click="openCreateUserModal">
            <i class="fas fa-user-plus mr-1.5" /> Nuevo Usuario
          </button>
        </div>
      </div>

      <!-- Tabla de Usuarios -->
      <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50/70 border-b border-gray-100 text-[11px] font-black uppercase text-gray-400 tracking-wider">
                <th class="py-4 px-6">Usuario</th>
                <th class="py-4 px-6">Correo</th>
                <th class="py-4 px-6">Rol</th>
                <th class="py-4 px-6">Tienda Asignada</th>
                <th class="py-4 px-6 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50 text-sm">
              <tr v-if="filteredUsers.length === 0">
                <td colspan="5" class="py-12 text-center text-gray-400">
                  <i class="fas fa-user-slash text-2xl text-gray-300 mb-2 block" />
                  No se encontraron usuarios con ese criterio.
                </td>
              </tr>
              <tr v-for="userItem in filteredUsers" :key="userItem.id" class="hover:bg-gray-50/50 transition-colors">
                <td class="py-4 px-6">
                  <div class="flex items-center gap-3">
                    <div class="w-9 h-9 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-xs flex-shrink-0 overflow-hidden">
                      <img v-if="userItem.photo" :src="userItem.photo" class="w-full h-full object-cover" />
                      <span v-else>{{ (userItem.first_name?.[0] || 'U') }}{{ (userItem.last_name?.[0] || '') }}</span>
                    </div>
                    <div>
                      <p class="font-bold text-gray-900 leading-tight">{{ userItem.name }}</p>
                      <span v-if="userItem.deleted_at" class="text-[10px] text-rose-500 font-bold uppercase">Eliminado</span>
                    </div>
                  </div>
                </td>
                <td class="py-4 px-6 text-gray-600 text-xs font-mono">{{ userItem.email }}</td>
                <td class="py-4 px-6">
                  <span :class="getRoleBadgeClass(userItem.role)" class="px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider">
                    {{ formatRoleName(userItem.role) }}
                  </span>
                </td>
                <td class="py-4 px-6 text-gray-600 text-xs">
                  {{ userItem.organization?.name || 'Todas / General' }}
                </td>
                <td class="py-4 px-6 text-right">
                  <div class="flex items-center justify-end gap-1.5">
                    <button class="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center shadow-sm" title="Editar" @click="openEditUserModal(userItem)">
                      <i class="fas fa-edit text-xs" />
                    </button>
                    <button v-if="!userItem.deleted_at && userItem.id !== authUser.id" class="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm" title="Eliminar" @click="deleteUserItem(userItem)">
                      <i class="fas fa-trash text-xs" />
                    </button>
                    <button v-if="userItem.deleted_at" class="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center shadow-sm" title="Restaurar" @click="restoreUserItem(userItem)">
                      <i class="fas fa-trash-restore text-xs" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ══ TAB 5: CAMPOS ADICIONALES ══ -->
    <div v-if="activeTab === 'campos-adicionales'" class="space-y-8 max-w-5xl transition-all duration-300">
      <div
        v-for="entity in [
          { id: 'order', label: 'Órdenes / Ventas', icon: 'fas fa-file-invoice', fields: orderFields },
          { id: 'client', label: 'Clientes', icon: 'fas fa-user-friends', fields: clientFields }
        ]" :key="entity.id" class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <div class="px-8 py-5 border-b border-gray-50 flex items-center justify-between bg-gradient-to-r from-gray-50/50 to-white">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500">
              <i :class="entity.icon" />
            </div>
            <div>
              <h3 class="text-xl font-extrabold text-gray-900">Campos para {{ entity.label }}</h3>
              <p class="text-xs text-gray-400">Atributos personalizados al crear {{ entity.id === 'order' ? 'una venta' : 'un cliente' }}</p>
            </div>
          </div>
          <button
            class="btn-orange shadow-md shadow-orange-100 text-xs"
            @click="showAddFieldModal = true; currentEntityType = entity.id"
          >
            <i class="fas fa-plus mr-1.5" />
            Nuevo Campo
          </button>
        </div>
        
        <div class="p-8">
          <div v-if="entity.fields.length === 0" class="flex flex-col items-center justify-center py-10 text-gray-400 bg-gray-50/30 rounded-2xl border-2 border-dashed border-gray-100">
            <div class="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
              <i :class="entity.icon" class="text-xl text-gray-300" />
            </div>
            <p class="font-bold text-sm text-gray-600">No hay campos configurados</p>
            <p class="text-xs text-gray-300">Empieza agregando un campo personalizado para {{ entity.label }}</p>
          </div>
          
          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              v-for="field in entity.fields"
              :key="field.id"
              class="group flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl hover:border-orange-200 hover:shadow-md hover:shadow-orange-50 transition-all duration-300"
            >
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-bold text-gray-800 text-sm">{{ field.field_label }}</span>
                  <span class="px-2 py-0.5 text-[10px] bg-orange-50 text-orange-600 font-bold rounded-lg uppercase tracking-wider">{{ field.field_type }}</span>
                </div>
                <div class="flex items-center gap-2 text-xs text-gray-400">
                  <span>Slug: <code class="bg-gray-50 px-1 rounded font-mono">{{ field.field_name }}</code></span>
                  <span v-if="field.is_required" class="w-1.5 h-1.5 rounded-full bg-rose-500" title="Requerido" />
                  <span v-if="field.is_required" class="text-rose-500 font-medium">Requerido</span>
                </div>
              </div>
              
              <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  class="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                  title="Editar"
                  @click="editField(field)"
                >
                  <i class="fas fa-edit text-xs" />
                </button>
                <button
                  class="w-8 h-8 flex items-center justify-center rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                  title="Eliminar"
                  @click="deleteField(field)"
                >
                  <i class="fas fa-trash text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ TAB 6: INTEGRACIONES ══ -->
    <div v-if="activeTab === 'integraciones'" class="max-w-4xl transition-all duration-300">
      <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div class="px-8 py-5 border-b border-gray-50 flex items-center gap-3 bg-gradient-to-r from-gray-50/50 to-white">
          <div class="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500">
            <i class="fas fa-plug text-lg" />
          </div>
          <div>
            <h3 class="text-xl font-extrabold text-gray-900">Integraciones y Pagos</h3>
            <p class="text-xs text-gray-400">Configura tus credenciales y pasarelas de pago (Pabilo, Binance Pay, API Key)</p>
          </div>
        </div>
        
        <form @submit.prevent="updateAccount">
          <div class="p-8 space-y-8">
            <!-- Pabilo -->
            <div class="bg-gray-50/70 rounded-2xl p-6 border border-gray-100">
              <div class="flex items-center gap-3 mb-6">
                <img src="https://pabilo.app/pabilo_favicon_light.png" alt="Pabilo" class="w-8 h-8 rounded-lg shadow-sm" onerror="this.src=''; this.className='fas fa-money-check-alt text-2xl text-blue-500'" />
                <div>
                  <h4 class="text-base font-bold text-gray-800">Verificación Automática de Pagos (Pabilo)</h4>
                  <p class="text-xs text-gray-400">Conciliación automática de Pagos Móviles y transferencias bancarias</p>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <text-input
                  v-model="form.pabilo_api_key"
                  :error="form.errors.pabilo_api_key"
                  label="API Key (Bearer Token)"
                  placeholder="sk_live_..."
                  class="[&_input]:rounded-xl font-mono text-xs"
                />
                <text-input
                  v-model="form.pabilo_user_bank_id"
                  :error="form.errors.pabilo_user_bank_id"
                  label="User Bank ID"
                  placeholder="65a7..."
                  class="[&_input]:rounded-xl font-mono text-xs"
                />
              </div>
              <p class="text-xs text-gray-400 mt-4">
                Estas credenciales permiten verificar de manera automática los pagos recibidos por tus clientes. Puedes obtenerlas en el <a href="https://pabilo.app" target="_blank" class="text-orange-500 hover:underline font-bold">panel de Pabilo</a>.
              </p>
            </div>

            <!-- Binance Pay -->
            <div class="bg-gray-50/70 rounded-2xl p-6 border border-gray-100">
              <div class="flex items-center gap-3 mb-6">
                <div class="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <i class="fab fa-btc text-yellow-600" />
                </div>
                <div>
                  <h4 class="text-base font-bold text-gray-800">Binance Pay</h4>
                  <p class="text-xs text-gray-400">Cobros en criptomonedas (USDT, BTC) a través de Binance</p>
                </div>
              </div>
              <div>
                <text-input
                  v-model="form.binance_api_key"
                  :error="form.errors.binance_api_key"
                  label="Binance API Key"
                  placeholder="Tu API Key de Binance..."
                  class="[&_input]:rounded-xl font-mono text-xs"
                />
              </div>
              <p class="text-xs text-gray-400 mt-4">
                Integra cobros directos en criptomonedas utilizando tu cuenta de Binance.
              </p>
            </div>

            <!-- API Externa de Realgestion -->
            <div class="bg-gray-50/70 rounded-2xl p-6 border border-gray-100">
              <div class="flex items-center gap-3 mb-6">
                <div class="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <i class="fas fa-key text-orange-600" />
                </div>
                <div>
                  <h4 class="text-base font-bold text-gray-800">API Externa de Realgestión</h4>
                  <p class="text-xs text-gray-400">Token de acceso para conectar sistemas y software externos</p>
                </div>
              </div>
              <div class="flex items-center gap-4 flex-wrap sm:flex-nowrap">
                <div class="flex-1 min-w-[200px]">
                  <text-input
                    :model-value="account.api_token || 'No se ha generado ninguna API Key'"
                    readonly
                    label="API Token de Acceso"
                    class="[&_input]:rounded-xl [&_input]:bg-white font-mono text-xs"
                  />
                </div>
                <div class="pt-6">
                  <button type="button" class="btn-orange whitespace-nowrap text-xs" @click="generateApiKey">
                    <i class="fas fa-sync-alt mr-1.5" /> {{ account.api_token ? 'Regenerar' : 'Generar' }} Token
                  </button>
                </div>
              </div>
              <p class="text-xs text-gray-400 mt-4">
                Utiliza este Token para autenticar peticiones de tus aplicaciones externas con el backend de Realgestión.
                <br /><strong class="text-orange-600">Importante:</strong> Al regenerar el Token, cualquier sistema conectado con la clave anterior dejará de sincronizar.
              </p>
            </div>
          </div>

          <div class="flex items-center justify-end px-8 py-5 bg-gray-50 border-t border-gray-100">
            <loading-button
              :loading="form.processing"
              class="btn-orange shadow-lg shadow-orange-100"
              type="submit"
            >
              <i class="fas fa-save mr-2" />
              Guardar Credenciales
            </loading-button>
          </div>
        </form>
      </div>
    </div>

    <!-- ══ MODAL PARA AGREGAR / EDITAR CAMPOS ADICIONALES ══ -->
    <div v-if="showAddFieldModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all duration-300">
      <div class="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
        <div class="px-8 py-5 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600">
              <i :class="editingField ? 'fas fa-edit' : 'fas fa-plus-circle'" />
            </div>
            <div>
              <h3 class="text-lg font-extrabold text-gray-900">
                {{ editingField ? 'Editar Atributo' : 'Nuevo Atributo' }}
              </h3>
              <p class="text-xs text-gray-400">Campo para {{ fieldForm.entity_type === 'order' ? 'Ventas' : 'Clientes' }}</p>
            </div>
          </div>
          <button class="text-gray-400 hover:text-gray-600" @click="closeFieldModal">
            <i class="fas fa-times" />
          </button>
        </div>
        
        <form class="p-8 space-y-4" @submit.prevent="saveField">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1.5">Entidad</label>
            <select
              v-model="fieldForm.entity_type"
              class="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400 bg-gray-50/50"
              :disabled="!!editingField"
            >
              <option value="order">Órdenes / Ventas</option>
              <option value="client">Clientes</option>
            </select>
          </div>
          
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1.5">Nombre Interno (Slug) *</label>
            <input
              v-model="fieldForm.field_name"
              type="text"
              class="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="ej: codigo_interno"
              required
            />
          </div>
          
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1.5">Etiqueta Visible en Formulario *</label>
            <input
              v-model="fieldForm.field_label"
              type="text"
              class="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="ej: Código Interno"
              required
            />
          </div>
          
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1.5">Tipo de Dato *</label>
            <select
              v-model="fieldForm.field_type"
              class="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
              required
            >
              <option value="text">Texto Corto</option>
              <option value="number">Valor Numérico</option>
              <option value="date">Fecha</option>
              <option value="select">Lista de Selección</option>
              <option value="textarea">Texto Largo (Área)</option>
              <option value="boolean">Interruptor (Sí / No)</option>
            </select>
          </div>
          
          <div v-if="fieldForm.field_type === 'select'">
            <label class="block text-xs font-bold text-gray-700 mb-1.5">Opciones de la Lista</label>
            <textarea
              v-model="fieldOptionsText"
              class="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
              rows="3"
              placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
            />
            <p class="text-[10px] text-gray-400 mt-1 italic">Escribe una opción por línea</p>
          </div>
          
          <div class="flex items-center gap-6 py-2 border-t border-gray-50">
            <label class="flex items-center cursor-pointer">
              <input v-model="fieldForm.is_required" type="checkbox" class="rounded text-orange-500 focus:ring-orange-400 mr-2" />
              <span class="text-xs font-bold text-gray-700">Requerido</span>
            </label>
            
            <label class="flex items-center cursor-pointer">
              <input v-model="fieldForm.is_active" type="checkbox" class="rounded text-orange-500 focus:ring-orange-400 mr-2" />
              <span class="text-xs font-bold text-gray-700">Activo</span>
            </label>
          </div>
          
          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-50">
            <button
              type="button"
              class="px-4 py-2 text-xs font-bold text-gray-400 hover:text-gray-600"
              @click="closeFieldModal"
            >
              Cancelar
            </button>
            <loading-button
              :loading="savingField"
              class="btn-orange shadow-md text-xs"
              type="submit"
            >
              {{ editingField ? 'Guardar Cambios' : 'Crear Atributo' }}
            </loading-button>
          </div>
        </form>
      </div>
    </div>

    <!-- ══ MODAL PARA CREAR / EDITAR USUARIO ══ -->
    <div v-if="showUserModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden">
        <div class="px-8 py-5 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600">
              <i :class="editingUser ? 'fas fa-user-edit' : 'fas fa-user-plus'" />
            </div>
            <div>
              <h3 class="text-lg font-extrabold text-gray-900">
                {{ editingUser ? 'Editar Usuario' : 'Nuevo Usuario' }}
              </h3>
              <p class="text-xs text-gray-400">Permisos y acceso a la plataforma</p>
            </div>
          </div>
          <button class="text-gray-400 hover:text-gray-600" @click="closeUserModal">
            <i class="fas fa-times" />
          </button>
        </div>

        <form class="p-8 space-y-4" @submit.prevent="saveUserModal">
          <div class="grid grid-cols-2 gap-4">
            <text-input v-model="userForm.first_name" :error="userForm.errors.first_name" label="Nombre *" placeholder="Nombre" class="[&_input]:rounded-xl" required />
            <text-input v-model="userForm.last_name" :error="userForm.errors.last_name" label="Apellido *" placeholder="Apellido" class="[&_input]:rounded-xl" required />
          </div>

          <text-input v-model="userForm.email" :error="userForm.errors.email" type="email" label="Correo Electrónico *" placeholder="usuario@empresa.com" class="[&_input]:rounded-xl" required />

          <text-input v-model="userForm.password" :error="userForm.errors.password" type="password" autocomplete="new-password" :label="editingUser ? 'Contraseña (Dejar en blanco para mantener)' : 'Contraseña *'" placeholder="Contraseña de acceso" class="[&_input]:rounded-xl" :required="!editingUser" />

          <div class="grid grid-cols-2 gap-4">
            <select-input v-model="userForm.role" :error="userForm.errors.role" label="Rol *" class="[&_select]:rounded-xl" required>
              <option value="admin">Administrador</option>
              <option value="sales">Vendedor</option>
              <option value="storer">Almacenista</option>
              <option value="financial">Finanzas</option>
            </select-input>

            <select-input v-model="userForm.organization_id" :error="userForm.errors.organization_id" label="Tienda Asignada" class="[&_select]:rounded-xl">
              <option :value="null">Todas / Sin tienda</option>
              <option v-for="org in organizations" :key="org.id" :value="org.id">{{ org.name }}</option>
            </select-input>
          </div>

          <div>
            <file-input v-model="userForm.photo" :error="userForm.errors.photo" type="file" accept="image/*" label="Foto de Perfil" class="[&_input]:rounded-xl" />
          </div>

          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-50">
            <button type="button" class="px-4 py-2 text-xs font-bold text-gray-400 hover:text-gray-600" @click="closeUserModal">
              Cancelar
            </button>
            <loading-button :loading="savingUser" class="btn-orange shadow-md text-xs" type="submit">
              {{ editingUser ? 'Guardar Cambios' : 'Crear Usuario' }}
            </loading-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import FileInput from '@/Shared/FileInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  
  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
    SelectInput,
    FileInput,
  },
  layout: Layout,

  props: {
    account: {
      type: Object,
      required: true,
    },
    authUser: {
      type: Object,
      default: () => ({}),
    },
    users: {
      type: Array,
      default: () => [],
    },
    organizations: {
      type: Array,
      default: () => [],
    },
    subscription: {
      type: Object,
      default: () => ({}),
    },
    additionals: {
      type: Object,
      default: () => ({}),
    },
    initialMode: {
      type: String,
      default: 'informacion-general',
    },
  },

  data() {
    return {
      activeTab: 'informacion-general',
      showAddFieldModal: false,
      editingField: null,
      currentEntityType: 'order',
      savingField: false,
      fieldOptionsText: '',

      // Panel de usuarios
      userSearchQuery: '',
      userFilterRole: '',
      showUserModal: false,
      editingUser: null,
      savingUser: false,

      businessTypes: [
        { id: 'retail', label: 'Minorista o detal' },
        { id: 'wholesale', label: 'Mayorista o distribuidora' },
        { id: 'restaurant', label: 'Restaurantes / Bares' },
        { id: 'services', label: 'Venta de servicios' },
        { id: 'subscriptions', label: 'Suscripciones / Membresias' },
      ],

      tabs: [
        { id: 'informacion-general', name: 'Información General', icon: 'fas fa-building' },
        { id: 'perfil', name: 'Perfil', icon: 'fas fa-user-circle' },
        { id: 'mi-suscripcion', name: 'Mi suscripción', icon: 'fas fa-crown' },
        { id: 'panel-usuarios', name: 'Panel de usuarios', icon: 'fas fa-users-cog' },
        { id: 'campos-adicionales', name: 'Campos adicionales', icon: 'fas fa-sliders-h' },
        { id: 'integraciones', name: 'Integraciones', icon: 'fas fa-plug' },
      ],

      defaultPlanFeatures: [
        'Ventas POS y Gestión de Pedidos',
        'Inventario y Control de Stock',
        'Gestión de Clientes y Cobranzas',
        'Reportes y Métricas Financieras',
        'Catálogo Digital en Línea',
      ],

      form: this.$inertia.form({
        _method: 'put',
        name: this.account.name,
        username: this.account.username,
        identification: this.account.identification,
        email: this.account.email,
        phone: this.account.phone,
        reasonName: this.account.reasonName,
        business_type: (() => {
          const raw = (this.account.business_type || '').toLowerCase().trim()
          if (raw === 'wholesale' || raw === 'mayorista') return 'wholesale'
          if (raw === 'restaurant' || raw === 'food' || this.account.restaurant_mode) return 'restaurant'
          if (raw === 'services' || raw === 'servicios') return 'services'
          if (raw === 'subscriptions' || raw === 'suscripciones') return 'subscriptions'
          return raw || 'retail'
        })(),
        address: this.account.address,
        sidebarColor: this.account.sidebarColor,
        textSidebarColor: this.account.textSidebarColor,
        textHoverSidebarColor: this.account.textHoverSidebarColor,
        logo: null,
        sellerCanModifyPrices: !!this.account.sellerCanModifyPrices,
        sellerHasOwnClientAgenda: !!this.account.sellerHasOwnClientAgenda,
        sellerCanEditProductPrices: !!this.account.sellerCanEditProductPrices,
        custom_rates: !!this.account.customRates,
        restaurant_mode: !!(this.account.restaurant_mode || this.account.restaurantMode || this.account.business_type === 'restaurant' || this.account.business_type === 'food'),
        pabilo_api_key: this.account.pabilo_api_key || '',
        pabilo_user_bank_id: this.account.pabilo_user_bank_id || '',
        binance_api_key: this.account.binance_api_key || '',
      }),

      profileForm: this.$inertia.form({
        _method: 'put',
        first_name: this.authUser?.first_name || '',
        last_name: this.authUser?.last_name || '',
        email: this.authUser?.email || '',
        password: '',
        role: this.authUser?.role || 'admin',
        organization_id: this.authUser?.organization_id || null,
        photo: null,
      }),

      userForm: this.$inertia.form({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'sales',
        organization_id: this.organizations[0]?.id || null,
        photo: null,
        redirect_back: 1,
      }),

      fieldForm: this.$inertia.form({
        entity_type: 'order',
        field_name: '',
        field_label: '',
        field_type: 'text',
        field_options: [],
        is_required: false,
        is_active: true,
        sort_order: 0,
      }),
    }
  },

  computed: {
    orderFields() {
      return this.additionals?.order || []
    },
    
    clientFields() {
      return this.additionals?.client || []
    },

    filteredUsers() {
      return (this.users || []).filter(u => {
        const matchesRole = !this.userFilterRole || u.role === this.userFilterRole
        const query = this.userSearchQuery.toLowerCase().trim()
        const matchesSearch = !query || 
          (u.name && u.name.toLowerCase().includes(query)) ||
          (u.email && u.email.toLowerCase().includes(query))
        return matchesRole && matchesSearch
      })
    },
  },

  created() {
    this.initActiveTab()
  },

  mounted() {
    window.addEventListener('popstate', this.handlePopState)
  },

  beforeUnmount() {
    window.removeEventListener('popstate', this.handlePopState)
  },

  methods: {
    initActiveTab() {
      const modeParam = new URLSearchParams(window.location.search).get('mode') || this.initialMode
      const normalized = this.normalizeMode(modeParam)
      this.activeTab = normalized
    },

    normalizeMode(mode) {
      const aliases = {
        'general': 'informacion-general',
        'information': 'informacion-general',
        'info': 'informacion-general',
        'informacion': 'informacion-general',
        'profile': 'perfil',
        'subscription': 'mi-suscripcion',
        'suscripcion': 'mi-suscripcion',
        'users': 'panel-usuarios',
        'usuarios': 'panel-usuarios',
        'additionals': 'campos-adicionales',
        'adicionales': 'campos-adicionales',
        'settings': 'informacion-general',
        'integrations': 'integraciones',
      }

      if (aliases[mode]) return aliases[mode]
      if (this.tabs.some(t => t.id === mode)) return mode
      return 'informacion-general'
    },

    selectTab(tabId) {
      this.activeTab = tabId
      const newUrl = `/account/${this.account.id}?mode=${tabId}`
      window.history.pushState({ mode: tabId }, '', newUrl)
    },

    handlePopState() {
      const modeParam = new URLSearchParams(window.location.search).get('mode')
      if (modeParam) {
        this.activeTab = this.normalizeMode(modeParam)
      }
    },

    updateAccount() {
      this.form.post(`/account/${this.account.id}`, {
        forceFormData: true,
        preserveScroll: true,
        onSuccess: () => {
          if (this.$toast) {
            this.$toast.success('Cuenta actualizada correctamente')
          }
        },
      })
    },

    updateProfile() {
      this.profileForm.post(`/users/${this.authUser.id}`, {
        forceFormData: true,
        preserveScroll: true,
        onSuccess: () => {
          this.profileForm.password = ''
          if (this.$toast) {
            this.$toast.success('Perfil actualizado correctamente')
          }
        },
      })
    },

    formatRoleName(role) {
      const roles = {
        admin: 'Administrador',
        sales: 'Vendedor',
        storer: 'Almacenista',
        financial: 'Finanzas',
      }
      return roles[role] || role
    },

    getRoleBadgeClass(role) {
      const classes = {
        admin: 'bg-orange-100 text-orange-800',
        sales: 'bg-blue-100 text-blue-800',
        storer: 'bg-purple-100 text-purple-800',
        financial: 'bg-emerald-100 text-emerald-800',
      }
      return classes[role] || 'bg-gray-100 text-gray-800'
    },

    openCreateUserModal() {
      this.editingUser = null
      this.userForm.reset()
      this.userForm.role = 'sales'
      this.userForm.organization_id = this.organizations[0]?.id || null
      this.userForm.redirect_back = 1
      this.showUserModal = true
    },

    openEditUserModal(user) {
      this.editingUser = user
      this.userForm.first_name = user.first_name
      this.userForm.last_name = user.last_name
      this.userForm.email = user.email
      this.userForm.password = ''
      this.userForm.role = user.role
      this.userForm.organization_id = user.organization_id
      this.userForm.photo = null
      this.showUserModal = true
    },

    closeUserModal() {
      this.showUserModal = false
      this.editingUser = null
      this.userForm.reset()
    },

    saveUserModal() {
      this.savingUser = true

      if (this.editingUser) {
        this.userForm._method = 'put'
        this.userForm.post(`/users/${this.editingUser.id}`, {
          forceFormData: true,
          preserveScroll: true,
          onSuccess: () => {
            this.closeUserModal()
            this.savingUser = false
            this.$inertia.reload({ only: ['users'] })
            if (this.$toast) {
              this.$toast.success('Usuario actualizado correctamente')
            }
          },
          onError: () => {
            this.savingUser = false
          },
        })
      } else {
        delete this.userForm._method
        this.userForm.post('/users', {
          forceFormData: true,
          preserveScroll: true,
          onSuccess: () => {
            this.closeUserModal()
            this.savingUser = false
            this.$inertia.reload({ only: ['users'] })
            if (this.$toast) {
              this.$toast.success('Usuario creado correctamente')
            }
          },
          onError: () => {
            this.savingUser = false
          },
        })
      }
    },

    deleteUserItem(user) {
      if (confirm(`¿Estás seguro de eliminar al usuario ${user.name}?`)) {
        this.$inertia.delete(`/users/${user.id}`, {
          preserveScroll: true,
          onSuccess: () => {
            this.$inertia.reload({ only: ['users'] })
            if (this.$toast) {
              this.$toast.success('Usuario eliminado correctamente')
            }
          },
        })
      }
    },

    restoreUserItem(user) {
      this.$inertia.put(`/users/${user.id}/restore`, {}, {
        preserveScroll: true,
        onSuccess: () => {
          this.$inertia.reload({ only: ['users'] })
          if (this.$toast) {
            this.$toast.success('Usuario restaurado correctamente')
          }
        },
      })
    },

    generateApiKey() {
      if (confirm('¿Estás seguro de generar una nueva API Key? Cualquier integración externa que use la clave anterior dejará de funcionar.')) {
        this.$inertia.post(`/account/${this.account.id}/generate-api-key`, {}, {
          preserveScroll: true,
          onSuccess: () => {
            if (this.$toast) {
              this.$toast.success('API Key generada correctamente')
            }
          },
        })
      }
    },

    editField(field) {
      this.editingField = field
      this.currentEntityType = field.entity_type
      this.fieldForm = this.$inertia.form({
        entity_type: field.entity_type,
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        field_options: field.field_options || [],
        is_required: !!field.is_required,
        is_active: !!field.is_active,
        sort_order: field.sort_order,
      })
      this.fieldOptionsText = (field.field_options || []).join('\n')
      this.showAddFieldModal = true
    },
    
    deleteField(field) {
      if (confirm('¿Estás seguro de que quieres eliminar este campo?')) {
        this.$inertia.delete(`/additionals/${field.id}`, {
          preserveScroll: true,
          onSuccess: () => {
            this.$inertia.reload({ only: ['additionals'] })
            if (this.$toast) {
              this.$toast.success('Campo eliminado correctamente')
            }
          },
        })
      }
    },
    
    closeFieldModal() {
      this.showAddFieldModal = false
      this.editingField = null
      this.fieldForm.reset()
      this.fieldOptionsText = ''
    },
    
    saveField() {
      if (this.fieldForm.field_type === 'select') {
        this.fieldForm.field_options = this.fieldOptionsText
          .split('\n')
          .map(option => option.trim())
          .filter(option => option.length > 0)
      }
      
      this.savingField = true
      
      if (this.editingField) {
        this.fieldForm.put(`/additionals/${this.editingField.id}`, {
          preserveScroll: true,
          onSuccess: () => {
            this.closeFieldModal()
            this.savingField = false
            this.$inertia.reload({ only: ['additionals'] })
            if (this.$toast) {
              this.$toast.success('Campo actualizado correctamente')
            }
          },
          onError: () => {
            this.savingField = false
          },
        })
      } else {
        this.fieldForm.post('/additionals', {
          preserveScroll: true,
          onSuccess: () => {
            this.closeFieldModal()
            this.savingField = false
            this.$inertia.reload({ only: ['additionals'] })
            if (this.$toast) {
              this.$toast.success('Campo creado correctamente')
            }
          },
          onError: () => {
            this.savingField = false
          },
        })
      }
    },
  },
}
</script>